import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";

import { useMutationWithError } from "@/utils/hooks/useMutationWithError";
import { useFlags } from "@/utils/launchDarkly/useFlags";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";

import {
  RemovePaymentGiftCardFromBookingInput,
  useRemovePaymentGiftCardFromBookingMutation,
} from "./__generated__/useRemovePaymentGiftCardFromBookingMutation.graphql";

export const useRemovePaymentGiftCardFromBooking = () => {
  const { showToast } = useToast();
  const { patronLoyalty } = useFlags();
  const [
    removePaymentGiftCardFromBooking,
    isRemovingPaymentGiftCardFromBooking,
  ] = useMutationWithError<useRemovePaymentGiftCardFromBookingMutation>(
    useRemovePaymentGiftCardFromBookingMutationString
  );

  const handleResult = (
    success: boolean | null | undefined,
    bookingId: string | null | undefined,
    successCallback?: (bookingId: string) => void
  ) => {
    if (success) {
      successCallback?.(bookingId ?? "");
      showToast({
        description: t`Gift card removed from booking.`,
        semanticStyle: "success",
        toastId: "remove-payment-gift-card-from-booking-success",
      });
    } else {
      showToast({
        description: t`Sorry. An error occurred while removing a gift card from the booking.`,
        semanticStyle: "error",
        toastId: "remove-payment-gift-card-from-booking-error",
      });
    }
  };

  const handleRemovePaymentGiftCardFromBooking = (
    input: RemovePaymentGiftCardFromBookingInput,
    successCallback?: (bookingId: string) => void
  ) => {
    removePaymentGiftCardFromBooking({
      variables: { input, patronLoyalty: !!patronLoyalty },
      onCompleted: (response) =>
        handleResult(
          response.responsePayload?.success,
          response.responsePayload?.booking?.id,
          successCallback
        ),
    });
  };

  return [
    handleRemovePaymentGiftCardFromBooking,
    isRemovingPaymentGiftCardFromBooking,
  ] as const;
};

export const useRemovePaymentGiftCardFromBookingMutationString = graphql`
  mutation useRemovePaymentGiftCardFromBookingMutation(
    $input: RemovePaymentGiftCardFromBookingInput!
    $patronLoyalty: Boolean!
  ) {
    responsePayload: removePaymentGiftCardFromBooking(input: $input) {
      success
      errors {
        field
        code
        message
      }
      booking {
        id
        totalDueAfterCredits
        projectedCreditApplications @include(if: $patronLoyalty) {
          amount
          paymentSource
          squareGiftCard {
            id
            ganLastFour
          }
          raincheck {
            id
          }
        }
        ... on StandardBooking {
          paymentGiftCards {
            edges {
              node {
                id
              }
            }
          }
        }
        ... on LessonBooking {
          paymentGiftCards {
            edges {
              node {
                id
              }
            }
          }
        }
      }
    }
  }
`;
